import { __assign, __awaiter, __generator, __read } from "tslib";
import { fetchFeatureGates } from '@office-start/feature-gates-service';
import { cancelUploadSession, createUploadSession, getUploadSessionStatus, uploadFragment, } from '@office-start/graph-service';
import { didActivityHaveExtraCapacity } from './didActivityHaveExtraCapacity';
import { doesActivityHaveRemainingCapacity } from './doesActivityHaveRemainingCapacity';
import { progressState } from './progressState';
import { updateProgress } from './updateProgress';
var MAX_RETRY_COUNT = 3;
var RETRY_INTERVAL = 1000;
export function advanceState(graphXhrOptions, session, state, cancellationToken, uploadConfig) {
    var sessionUrl = state.sessionUrl, nextExpectedRanges = state.nextExpectedRanges;
    var getNextState;
    if (!sessionUrl) {
        getNextState = createSessionState(graphXhrOptions, session, state);
    }
    else if (!nextExpectedRanges) {
        getNextState = getStatusState(graphXhrOptions, state, session.sessionId);
    }
    else {
        getNextState = uploadItemState(graphXhrOptions, session, state, cancellationToken, uploadConfig);
    }
    return getNextState.then(function (nextState) {
        if (nextState.error) {
            // Determine whether or not recovery is possible and update the state accordingly.
            return handleGeneralErrorState(nextState, cancellationToken, uploadConfig);
        }
        else {
            // The step was successful, so reset the retry behavior.
            return getState(nextState, {
                waitDuration: undefined,
                clientAttemptsRemaining: undefined,
                serverAttemptsRemaining: undefined,
            });
        }
    });
}
/**
 * Creates an upload session and updates state.
 */
function createSessionState(graphXhrOptions, session, state) {
    return createUploadSession(graphXhrOptions, session.file, session.sessionId, undefined, session.path, true, session.driveId).then(function (response) {
        return getState(state, {
            nextExpectedRanges: response.nextExpectedRanges,
            sessionUrl: response.uploadUrl,
        });
    }, function (error) {
        return getState(state, {
            // TODO: Should we allow the user to customize the conflict behavior?
            error: error,
        });
    });
}
function getStatusState(graphXhrOptions, state, sessionId) {
    if (!state.sessionUrl) {
        return Promise.reject('Empty sessionUrl');
    }
    return getUploadSessionStatus(graphXhrOptions, state.sessionUrl, sessionId).then(function (response) {
        return getState(state, {
            nextExpectedRanges: response
                .nextExpectedRanges,
        });
    }, function (error) {
        return getState(state, {
            error: error,
        });
    });
}
/**
 * Performs a fragment upload or cancels the session and updates state.
 */
function uploadItemState(graphXhrOptions, session, state, cancellationToken, uploadConfig) {
    var fileSize = session.file.size;
    var nextRange = getNextRange(fileSize, state.nextExpectedRanges);
    var pendingNextState;
    // TODO: Allow commits of finished uploads once the api onboards to graph
    if (nextRange && nextRange.start < fileSize) {
        pendingNextState = uploadFragmentState(graphXhrOptions, nextRange, session, state, cancellationToken, uploadConfig);
    }
    else {
        // There is nothing left to upload, but it is not possible to commit.
        // In this case, clear the session and restart since this is the only way to finish the upload.
        pendingNextState = cancelSessionState(graphXhrOptions, state, session.sessionId);
    }
    return pendingNextState.then(function (nextState) {
        if (nextState.error) {
            return handleUploadItemErrorState(nextState, cancellationToken, uploadConfig);
        }
        return nextState;
    });
}
/**
 * Uploads a fragment and updates state.
 */
function uploadFragmentState(graphXhrOptions, nextRange, session, state, manualcancellationToken, uploadConfig) {
    var _a;
    return __awaiter(this, void 0, void 0, function () {
        var sessionUrl, _b, speed, featureGates, isRetryEnabled, startByte, suggestedEndByte, fragmentSize, endByte, timeout, startTime, expirationTime, progressPoints, bandwidthVerifier, file, fragmentCancellationToken;
        var _this = this;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    sessionUrl = state.sessionUrl, _b = state.speed, speed = _b === void 0 ? uploadConfig.initialSpeed || 0 : _b;
                    return [4 /*yield*/, fetchFeatureGates()];
                case 1:
                    featureGates = _c.sent();
                    isRetryEnabled = ((_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result.uploadRetryGates) === null || _a === void 0 ? void 0 : _a.uploadRetryEnabled) || false;
                    if (!sessionUrl) {
                        return [2 /*return*/, Promise.reject('Empty sessionUrl')];
                    }
                    startByte = nextRange.start, suggestedEndByte = nextRange.end;
                    updateProgress(startByte, session, progressState(session.lastLoaded, session.fileSize));
                    fragmentSize = getFragmentSize(speed, uploadConfig.maxFragmentSize, uploadConfig.discreteChunkSize, uploadConfig.minFragmentSize);
                    endByte = Math.min(startByte + fragmentSize, suggestedEndByte);
                    timeout = speed > uploadConfig.minSpeed
                        ? uploadConfig.fragmentTimeoutInMs
                        : uploadConfig.fragmentTimeoutInMs *
                            uploadConfig.slowFragmentTimeoutMultiplier;
                    startTime = performance.now();
                    expirationTime = startTime + timeout;
                    progressPoints = 0;
                    bandwidthVerifier = {
                        startTime: startTime,
                        expirationTime: expirationTime,
                        progressPoints: progressPoints,
                        totalBytes: endByte - startByte,
                    };
                    file = session.file;
                    fragmentCancellationToken = {
                        cancel: function () { },
                        onCancel: function () { return new Promise(function () { }); },
                    };
                    manualcancellationToken.onCancel(function () {
                        fragmentCancellationToken.cancel();
                    });
                    return [2 /*return*/, new Promise(function (resolve) { return __awaiter(_this, void 0, void 0, function () {
                            function retryUploadFragment() {
                                return __awaiter(this, void 0, void 0, function () {
                                    var isError, retry, asyncFunc, newUploadState;
                                    return __generator(this, function (_a) {
                                        switch (_a.label) {
                                            case 0:
                                                isError = true;
                                                retry = 0;
                                                asyncFunc = function () {
                                                    return new Promise(function (resolve) {
                                                        setTimeout(function () {
                                                            tryUpload()
                                                                .then(function (d) {
                                                                resolve(d);
                                                            })
                                                                .catch(function (e) {
                                                                resolve({ error: e });
                                                            });
                                                        }, RETRY_INTERVAL);
                                                    });
                                                };
                                                _a.label = 1;
                                            case 1:
                                                if (!(isError && retry < MAX_RETRY_COUNT)) return [3 /*break*/, 3];
                                                return [4 /*yield*/, asyncFunc()];
                                            case 2:
                                                newUploadState = _a.sent();
                                                if (!newUploadState.error) {
                                                    isError = false;
                                                    return [2 /*return*/, newUploadState];
                                                }
                                                retry++;
                                                return [3 /*break*/, 1];
                                            case 3: return [2 /*return*/];
                                        }
                                    });
                                });
                            }
                            var onUploadProgress, tryUpload, handleSuccess;
                            var _this = this;
                            return __generator(this, function (_a) {
                                onUploadProgress = function (progressEvent) {
                                    var lastByte = startByte + progressEvent.loaded;
                                    if (speed > uploadConfig.minSpeed &&
                                        !doesActivityHaveRemainingCapacity(lastByte - startByte, bandwidthVerifier)) {
                                        // If it is not possible to upload the remaining bytes in time,
                                        // give up now and lower the fragment size for the next attempt.
                                        fragmentCancellationToken.cancel();
                                        resolve(getState(state, {
                                            speed: Math.max(speed * uploadConfig.slowSpeedDowngradeRatio, uploadConfig.minSpeed),
                                            nextExpectedRanges: undefined,
                                        }));
                                    }
                                    updateProgress(lastByte, session, progressState(session.lastLoaded, session.fileSize));
                                };
                                tryUpload = function () { return __awaiter(_this, void 0, void 0, function () {
                                    return __generator(this, function (_a) {
                                        switch (_a.label) {
                                            case 0: return [4 /*yield*/, uploadFragment(graphXhrOptions, sessionUrl, startByte, endByte, file.size, file.slice(startByte, endByte), session.sessionId, onUploadProgress)];
                                            case 1: return [2 /*return*/, _a.sent()];
                                        }
                                    });
                                }); };
                                tryUpload()
                                    .then(function (result) {
                                    handleSuccess(result);
                                })
                                    .catch(function (error) { return __awaiter(_this, void 0, void 0, function () {
                                    var retryUpload;
                                    return __generator(this, function (_a) {
                                        switch (_a.label) {
                                            case 0:
                                                if (!!isRetryEnabled) return [3 /*break*/, 1];
                                                resolve(getState(state, {
                                                    error: error,
                                                }));
                                                return [3 /*break*/, 3];
                                            case 1: return [4 /*yield*/, retryUploadFragment()];
                                            case 2:
                                                retryUpload = _a.sent();
                                                if (!retryUpload) {
                                                    resolve(getState(state, {
                                                        error: error,
                                                    }));
                                                }
                                                else {
                                                    handleSuccess(retryUpload);
                                                }
                                                _a.label = 3;
                                            case 3: return [2 /*return*/];
                                        }
                                    });
                                }); });
                                handleSuccess = function (result) {
                                    updateProgress(endByte, session, progressState(session.lastLoaded, session.fileSize));
                                    if (isDriveItem(result)) {
                                        state = getState(state, {
                                            item: result,
                                        });
                                    }
                                    else {
                                        state = getState(state, {
                                            nextExpectedRanges: result.nextExpectedRanges,
                                            // If the upload had extra time, then it should be safe to increase the fragment size.
                                            speed: Math.min(didActivityHaveExtraCapacity(bandwidthVerifier)
                                                ? speed * uploadConfig.speedUpgradeRatio
                                                : speed, 1),
                                        });
                                    }
                                    resolve(state);
                                };
                                return [2 /*return*/];
                            });
                        }); })];
            }
        });
    });
}
function cancelSessionState(graphXhrOptions, state, sessionId) {
    if (!state.sessionUrl) {
        return Promise.reject('Empty sessionUrl');
    }
    return cancelUploadSession(graphXhrOptions, state.sessionUrl, sessionId).then(function () {
        return getState(state, {
            sessionUrl: undefined,
        });
    }, function () {
        return getState(state, {
            sessionUrl: undefined,
        });
    });
}
/**
 * Handles a fragment upload or commit error, either by resetting some state or failing out.
 */
function handleUploadItemErrorState(state, cancellationToken, uploadConfig) {
    var _a = state.speed, speed = _a === void 0 ? 1 : _a;
    // All fragment upload errors should reset the known progress.
    state = getState(state, {
        nextExpectedRanges: undefined,
    });
    return handleErrorState(state, function (code) {
        switch (code) {
            case 706 /* HttpStatusCode.Offline */:
            case 0 /* HttpStatusCode.Unknown */:
                // The message was likely too large to get through. Dramatically reduce the size.
                return window.navigator.onLine
                    ? Promise.resolve(getState(state, {
                        speed: Math.max(speed * uploadConfig.speedDowngradeRatio, uploadConfig.minSpeed),
                    }))
                    : undefined;
            case 'uploadSessionFailed':
            case 'uploadSessionNotFound':
            case 404 /* HttpStatusCode.NotFound */:
                // Upload session is somehow gone. Need to start over.
                return Promise.resolve(getState(state, {
                    error: undefined,
                    sessionUrl: undefined,
                }));
            //case 'entityTagDoesNotMatch':
            //case 'itemAlreadyExists':
            //case 409:
            //case 412:
            // TODO: Should we allow the user to customize conflict behavior?
            case 'invalidRange':
            case 416 /* HttpStatusCode.RangeNotSatisfiable */:
                // Fragment overlap. Need to wait for a previous fragment upload to resolve.
                return wait(uploadConfig.standardWaitDurationInMs, cancellationToken).then(function () {
                    return getState(state, {
                        error: undefined,
                    });
                });
        }
        return undefined;
    });
}
/**
 * Handles a general API error, either by waiting and resetting some state or failing out.
 */
function handleGeneralErrorState(state, cancellationToken, uploadConfig) {
    var _a = state.waitDuration, waitDuration = _a === void 0 ? uploadConfig.initialWaitDurationInMs : _a, _b = state.clientAttemptsRemaining, clientAttemptsRemaining = _b === void 0 ? uploadConfig.numClientAttempts : _b, _c = state.serverAttemptsRemaining, serverAttemptsRemaining = _c === void 0 ? uploadConfig.numServerAttempts : _c;
    return handleErrorState(state, function (code) {
        switch (code) {
            case 'unauthenticated':
            case 401 /* HttpStatusCode.Unauthorized */:
            case 'lockMismatch':
            case 423 /* HttpStatusCode.Locked */:
            case 501 /* HttpStatusCode.NotImplemented */:
            case 'quotaLimitReached':
            case 507 /* HttpStatusCode.InsufficientStorage */:
            case 706 /* HttpStatusCode.Offline */:
                return undefined;
        }
        if (typeof code === 'number') {
            if (code >= 400 && code < 500 /* Client error */) {
                // Retry is not allowed.
                return undefined;
            }
            if (code === 0 ||
                (code >= 500 && code < 600) /* Transient server error */) {
                if (!serverAttemptsRemaining) {
                    return undefined;
                }
                // Server error. Wait and retry indefinitely via exponential back-off.
                return wait(waitDuration, cancellationToken, true).then(function () {
                    return getState(state, {
                        error: undefined,
                        waitDuration: Math.min(waitDuration * 2, uploadConfig.maxWaitDurationInMs),
                        serverAttemptsRemaining: Math.max(serverAttemptsRemaining - 1, 0),
                    });
                });
            }
            if (!clientAttemptsRemaining) {
                // Out of retry attempts.
                return undefined;
            }
            // Retryable client error. Retry a finite amount of times.
            return wait(uploadConfig.standardWaitDurationInMs, cancellationToken).then(function () {
                return getState(state, {
                    error: undefined,
                    waitDuration: undefined,
                    clientAttemptsRemaining: Math.max(clientAttemptsRemaining - 1, 0),
                });
            });
        }
        return undefined;
    });
}
function handleErrorState(state, _resolveError) {
    // const { error } = state;
    // TODO: achieve parity with resolveError code in OfficeHome
    // let resolution = GraphErrorResolver.resolveGraphError(error, resolveError);
    // if (resolution === undefined) {
    //     resolution = Promise.resolve(state);
    // }
    return Promise.resolve(state);
}
/**
 * Gets an incremented state based on a current state and a delta.
 */
function getState(current, increment) {
    return __assign(__assign({}, current), increment);
}
/**
 * Gets the next valid byte range for the upload given the expected ranges from the server.
 */
function getNextRange(fileSize, nextExpectedRanges) {
    if (nextExpectedRanges === void 0) { nextExpectedRanges = []; }
    return nextExpectedRanges.reduce(function (nextRange, rangeInput) {
        // rangeInput e.g. '0-', '1024-4095'
        var _a = __read(rangeInput.split('-', 2), 2), startInput = _a[0], endInput = _a[1];
        // startInput e.g. '0', '1024'
        // endInput e.g. '', '4095'
        var startByte = parseInt(startInput, 10);
        // startByte e.g. 0, 1024
        var range;
        if (!nextRange ||
            (startByte < nextRange.start && startByte < fileSize)) {
            var endByte = endInput === ''
                ? fileSize
                : Math.min(parseInt(endInput, 10) + 1, // endInput is the *index* of the last byte
                fileSize);
            // endByte e.g. 3556, 4096
            range = {
                start: startByte,
                end: endByte,
            };
        }
        else {
            range = nextRange;
        }
        return range;
    }, undefined);
}
/**
 * Determines whether the object is a OneDrive item (as opposed to a status response)
 */
function isDriveItem(itemOrOther) {
    return !!itemOrOther.id;
}
/**
 * Determines the fragment size to use given a current speed value.
 */
function getFragmentSize(speed, maxFragmentSize, discreteChunkSize, minFragmentSize) {
    var rawFragmentSize = speed * maxFragmentSize;
    var normalizedFragmentSize;
    if (rawFragmentSize > discreteChunkSize) {
        // If the fragment size is greater than the discrete chunk size, always use a multiple of
        // the discrete chunk size.
        normalizedFragmentSize =
            rawFragmentSize - (rawFragmentSize % discreteChunkSize);
    }
    else {
        // Otherwise, use a multiple of the minimum chunk size.
        normalizedFragmentSize =
            rawFragmentSize - (rawFragmentSize % minFragmentSize);
    }
    return Math.min(Math.max(normalizedFragmentSize, minFragmentSize), maxFragmentSize);
}
function wait(duration, cancellationToken, allowWakeWhenOnline) {
    if (allowWakeWhenOnline === void 0) { allowWakeWhenOnline = false; }
    var timeoutId;
    var timeoutPromise = new Promise(function (resolve) {
        timeoutId = setTimeout(function () {
            resolve();
        }, duration);
    });
    var onOnline;
    var wakePromise = new Promise(function (resolve) {
        onOnline = function () {
            if (allowWakeWhenOnline) {
                resolve();
            }
        };
        window.addEventListener('online', onOnline);
    });
    var cancelPromise = cancellationToken
        ? cancellationToken.onCancel(function () { return Promise.reject(null); })
        : new Promise(function () {
            /*Never resolve*/
        });
    return new Promise(function (resolve) {
        Promise.race([timeoutPromise, wakePromise, cancelPromise])
            .then(function () {
            resolve();
        }, function () {
            // Don't resolve on cancel
        })
            .then(function () {
            window.removeEventListener('online', onOnline);
            clearTimeout(timeoutId);
        });
    });
}
//# sourceMappingURL=advanceState.js.map